// src/components/ContactForm.jsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { contactRequestSchema } from "../validation/contactRequest.schema";
import api from "../api/axios";
import { toast } from "react-toastify";

export default function ContactForm({
  vehicleId = null,
  regNumber = null,
  onSuccess,
}) {
  const [requestId, setRequestId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(contactRequestSchema, {
      context: { requiresPayment: false },
    }),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      // Build payload; only include vehicle/regNumber if provided
      const payload = { ...data };
      if (vehicleId) {
        payload.vehicle = vehicleId;
        payload.regNumber = regNumber;
      }

      // Send request
      const response = await api.post("/requests", payload);
      const { requestId: newRequestId } = response.data;

      // Show thank-you state
      setRequestId(newRequestId);
      toast.success(`Request sent! Your reference ID is ${newRequestId}.`);
      reset();

      if (onSuccess) onSuccess(newRequestId);
    } catch (err) {
      console.error("Request failed", err);
      toast.error("Oops—something went wrong. Please try again.");
    }
  };

  // ────────────────────────────────────────────────────────────────
  // THANK-YOU SCREEN
  if (requestId) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <h2 className="text-brandAccent text-2xl font-semibold mb-2">
          Thank you!
        </h2>
        <p>Your request has been submitted successfully.</p>

        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
          <p className="text-primary">
            Reference ID:{" "}
            <span className="font-mono font-bold">{requestId}</span>
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Please save this for any follow-up questions.
          </p>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  // FORM SCREEN
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 max-w-2xl mx-auto">
      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium">
          Your email address*
        </label>
        <input
          id="email"
          type="email"
          {...register("email")}
          placeholder="you@example.com"
          className="text-primary mt-1 block w-full rounded-full border px-3 py-2"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-danger">{errors.email.message}</p>
        )}
      </div>

      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium">
          Full Name*
        </label>
        <input
          id="fullName"
          {...register("fullName")}
          placeholder="John Doe"
          className="text-primary mt-1 block w-full rounded-full border px-3 py-2"
        />
        {errors.fullName && (
          <p className="mt-1 text-sm text-danger">{errors.fullName.message}</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium">
          Phone*
        </label>
        <input
          id="phone"
          {...register("phone")}
          placeholder="+44 1234 567890"
          className="text-primary mt-1 block w-full rounded-full border px-3 py-2"
        />
        {errors.phone && (
          <p className="mt-1 text-sm text-danger">{errors.phone.message}</p>
        )}
      </div>

      {/* Message */}
      <div className="lg:col-span-2">
        <label htmlFor="message" className="block text-sm font-medium">
          Description*
        </label>
        <p className="text-sm text-white/50 mb-3">As well as you can, please detail what your issue is</p>
        <textarea
          id="message"
          {...register("message")}
          rows={4}
          className="text-primary mt-1 block w-full rounded-3xl border px-3 py-2"
        />
        {errors.message && (
          <p className="mt-1 text-sm text-danger">{errors.message.message}</p>
        )}
      </div>

      {/* Submit */}
      <div className="w-full">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-full px-6 py-2 bg-accent text-white hover:opacity-90"
        >
          {isSubmitting ? "Sending…" : "Send Request"}
        </button>
      </div>
    </form>
  );
}
